import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import { Booking } from './types';
import { archiveKeepsOccupying } from './archiveKeepsOccupying';
import { useAnonymizeBooking, useArchiveBooking, useRestoreBooking } from '../../../flows/bookingArchiveFlows';

export type BookingArchiveMode = 'archive' | 'restore' | 'anonymize';

interface Props {
    mode: BookingArchiveMode | null;
    booking: Booking | null;
    onClose: () => void;
    onDone: () => void;
}

const REASON_OPTIONS = ['resolved', 'test_booking', 'duplicate', 'customer_request', 'other'];
const NOTE_MAX_LENGTH = 200;

const BookingArchiveDialog: React.FC<Props> = ({ mode, booking, onClose, onDone }) => {
    const { t } = useTranslation();
    const archiveBooking = useArchiveBooking();
    const restoreBooking = useRestoreBooking();
    const anonymizeBooking = useAnonymizeBooking();

    const [reason, setReason] = useState('resolved');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (mode === 'archive') {
            setReason('resolved');
            setNote('');
        }
    }, [mode, booking?.bookingId]);

    if (!mode || !booking) return null;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            let ok = false;
            if (mode === 'archive') {
                ok = await archiveBooking(booking.bookingId, reason, note.trim() || undefined);
            } else if (mode === 'restore') {
                ok = await restoreBooking(booking.bookingId);
            } else {
                ok = await anonymizeBooking(booking.bookingId);
            }
            if (ok) {
                onDone();
                onClose();
            }
        } finally {
            setLoading(false);
        }
    };

    const title =
        mode === 'archive'
            ? t('bookings.archive.dialog.title')
            : mode === 'restore'
                ? t('bookings.archive.restore_dialog.title')
                : t('bookings.archive.anonymize_dialog.title');

    const confirmLabel =
        mode === 'archive'
            ? t('bookings.archive.dialog.confirm')
            : mode === 'restore'
                ? t('bookings.archive.restore_dialog.confirm')
                : t('bookings.archive.anonymize_dialog.confirm');

    return (
        <Dialog open onClose={loading ? undefined : onClose} maxWidth={'xs'} fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                {mode === 'archive' && (
                    <>
                        <Typography variant={'body2'}>
                            {t('bookings.archive.dialog.description')}
                        </Typography>
                        {archiveKeepsOccupying(booking) && (
                            <p
                                role={'note'}
                                className={'text-xs text-foreground bg-foreground/5 border border-foreground/10 rounded-lg px-4 py-3'}
                            >
                                {t('bookings.archive.dialog.still_occupying')}
                            </p>
                        )}
                        <FormControl fullWidth size={'small'}>
                            <InputLabel id={'archive-reason-label'}>
                                {t('bookings.archive.dialog.reason_label')}
                            </InputLabel>
                            <Select
                                labelId={'archive-reason-label'}
                                label={t('bookings.archive.dialog.reason_label')}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            >
                                {REASON_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {t(`bookings.archive.dialog.reason_options.${opt}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('bookings.archive.dialog.note_label')}
                            value={note}
                            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                            multiline
                            minRows={2}
                            fullWidth
                            size={'small'}
                            inputProps={{ maxLength: NOTE_MAX_LENGTH }}
                        />
                    </>
                )}
                {mode === 'restore' && (
                    <Typography variant={'body2'}>
                        {t('bookings.archive.restore_dialog.description')}
                    </Typography>
                )}
                {mode === 'anonymize' && (
                    <Typography variant={'body2'}>
                        {t('bookings.archive.anonymize_dialog.description')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <CustomButton
                    title={t('bookings.archive.dialog.cancel')}
                    onClick={onClose}
                    isFilled={false}
                    disabled={loading}
                />
                <CustomButton
                    title={confirmLabel}
                    onClick={handleConfirm}
                    loading={loading}
                    variant={mode === 'anonymize' ? 'delete' : undefined}
                />
            </DialogActions>
        </Dialog>
    );
};

export default BookingArchiveDialog;
